/**
 * Area Download — modale richiesta documento (#am-download-modal).
 *
 * L'apertura/chiusura del modale è dichiarativa (data-bs-toggle sul bottone
 * .js-download-request, Bootstrap Modal già bundlato in bootstrap.js). Qui
 * facciamo il passaggio dati che il CSS non può fare:
 *  - all'apertura copiamo data-doc-id nel campo hidden Formidable `docid`
 *    e data-doc-title nel sottotitolo del modale;
 *  - alla chiusura ripuliamo il sottotitolo.
 *
 * Inoltre risolviamo un problema di UX: il form Formidable non è in AJAX,
 * quindi il submit ricarica la pagina — il modale si chiude e l'utente non
 * vede né gli errori né la conferma. Perciò:
 *  1. validiamo lato client prima dell'invio e, se qualcosa non va, facciamo
 *     preventDefault(): il modale resta aperto e gli errori sono visibili
 *     accanto ai campi;
 *  2. se il server ha comunque risposto con errori (o con il messaggio di
 *     conferma), al caricamento riapriamo il modale sul feedback.
 *
 * I messaggi non sono hardcoded: usiamo quelli che Formidable espone sugli
 * input (data-reqmsg / data-invmsg, già tradotti nelle impostazioni del form)
 * con fallback sul messaggio nativo del browser, localizzato di suo.
 */
(function () {
	'use strict';

	var modal = document.getElementById('am-download-modal');
	if (!modal) {
		return;
	}

	var docTitleEl = modal.querySelector('.js-download-doc-title');

	function getDocIdInput() {
		// Hidden field Formidable con field key `docid` → id="field_docid".
		// Il form viene ri-renderizzato dopo il submit AJAX: query a ogni apertura.
		return modal.querySelector('input[type="hidden"][id^="field_docid"]');
	}

	modal.addEventListener('show.bs.modal', function (event) {
		var trigger = event.relatedTarget;
		if (!trigger || !trigger.classList.contains('js-download-request')) {
			return;
		}

		var input = getDocIdInput();
		if (input) {
			input.value = trigger.getAttribute('data-doc-id') || '';
		}

		if (docTitleEl) {
			docTitleEl.textContent = trigger.getAttribute('data-doc-title') || '';
		}
	});

	modal.addEventListener('hidden.bs.modal', function () {
		if (docTitleEl) {
			docTitleEl.textContent = '';
		}
	});

	// ---------------------------------------------------------------------
	// Validazione client-side (evita il reload che chiuderebbe il modale)
	// ---------------------------------------------------------------------

	var ERROR_MARKER = 'js-dl-error';

	function fieldWrap(input) {
		return input.closest('.frm_form_field') || input.parentNode;
	}

	// Campi dentro sezioni nascoste (o l'honeypot .frm_verify) non si validano.
	function isVisible(input) {
		return !!fieldWrap(input).offsetParent;
	}

	function isFilled(input) {
		if (input.type === 'checkbox' || input.type === 'radio') {
			return input.checked;
		}
		return input.value.trim() !== '';
	}

	// Messaggio "campo obbligatorio": quello di Formidable, altrimenti quello
	// nativo del browser (già nella lingua dell'utente).
	function requiredMessage(input) {
		var msg = input.getAttribute('data-reqmsg');
		if (msg) {
			return msg;
		}
		var wasRequired = input.required;
		input.required = true;
		msg = input.validationMessage;
		input.required = wasRequired;
		return msg;
	}

	function clearError(input) {
		var wrap = fieldWrap(input);
		var msg = wrap.querySelector('.' + ERROR_MARKER);
		if (msg) {
			msg.parentNode.removeChild(msg);
		}
		wrap.classList.remove('frm_blank_field');
		input.removeAttribute('aria-invalid');
	}

	function addError(input, message) {
		var wrap = fieldWrap(input);
		wrap.classList.add('frm_blank_field');
		input.setAttribute('aria-invalid', 'true');

		// Un solo messaggio per campo (es. Nome ha due sub-input, first/last).
		if (wrap.querySelector('.' + ERROR_MARKER)) {
			return;
		}
		var error = document.createElement('div');
		error.className = 'frm_error ' + ERROR_MARKER;
		error.textContent = message;
		wrap.appendChild(error);
	}

	function validate(form) {
		var invalid = [];
		var inputs = form.querySelectorAll('input:not([type="hidden"]), select, textarea');

		Array.prototype.forEach.call(inputs, function (input) {
			clearError(input);

			if (input.disabled || !isVisible(input)) {
				return;
			}

			// Obbligatorio: Formidable marca i campi con aria-required.
			if (input.getAttribute('aria-required') === 'true' && !isFilled(input)) {
				addError(input, requiredMessage(input));
				invalid.push(input);
				return;
			}

			// Formato (email, url, pattern…): validazione nativa del browser.
			if (isFilled(input) && typeof input.checkValidity === 'function' && !input.checkValidity()) {
				addError(input, input.getAttribute('data-invmsg') || input.validationMessage);
				invalid.push(input);
			}
		});

		// Campo "conferma email": deve combaciare con l'originale.
		var confirmField = form.querySelector('.frm_conf_field input');
		if (confirmField && isVisible(confirmField) && isFilled(confirmField)) {
			var name = confirmField.name.replace('[conf_', '[');
			var original = form.querySelector('[name="' + name + '"]');
			if (original && original.value.trim() !== confirmField.value.trim()) {
				addError(confirmField, confirmField.getAttribute('data-invmsg') || '');
				invalid.push(confirmField);
			}
		}

		return invalid;
	}

	var form = modal.querySelector('form');

	if (form) {
		form.addEventListener('submit', function (event) {
			var invalid = validate(form);
			if (!invalid.length) {
				return; // Tutto ok: prosegue il submit di Formidable.
			}

			// Blocca l'invio (e gli handler di Formidable) prima del reload.
			event.preventDefault();
			event.stopPropagation();

			invalid[0].focus();
		});

		// L'errore sparisce appena l'utente corregge il campo.
		form.addEventListener('input', function (event) {
			if (event.target.getAttribute('aria-invalid') === 'true') {
				clearError(event.target);
			}
		});
		form.addEventListener('change', function (event) {
			if (event.target.getAttribute('aria-invalid') === 'true') {
				clearError(event.target);
			}
		});
	}

	// ---------------------------------------------------------------------
	// Feedback dal server: se la pagina è tornata con errori o con il
	// messaggio di conferma, riapriamo il modale così l'utente li vede.
	// ---------------------------------------------------------------------

	var feedback = modal.querySelector('.frm_error_style, .frm_message, .frm_error');
	if (feedback) {
		var Modal = (window.understrap && window.understrap.Modal) ||
			(window.bootstrap && window.bootstrap.Modal);

		if (Modal) {
			Modal.getOrCreateInstance(modal).show();
		}
	}
})();
